import axios from 'axios';
import API_CONFIG from '../config/api';

// Use centralized API configuration
const baseURL = API_CONFIG.API_BASE;

// Create axios instance with base configuration
const api = axios.create({
  baseURL,
  timeout: 10000,
  headers: { 'Content-Type': 'application/json' },
});

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    // AI generation endpoints can take longer than typical API calls.
    // Axios default timeout is 10s; override for RFP AI calls.
    if (
      config.url &&
      (config.url.includes('/rfp/generate') ||
        config.url.includes('/rfp/create'))
    ) {
      config.timeout = 120000; // 2 minutes
    }

    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle common errors
api.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    //console.log('API Error:', error);
    //console.log('Error response:', error.response);
    //console.log('Error status:', error.response?.status);
    
    if (error.response?.status === 401) {
      // Token expired or invalid
      //console.log('401 error - redirecting to login');
      localStorage.removeItem('token');
      localStorage.removeItem('refreshToken');
      localStorage.removeItem('user');
      localStorage.removeItem('isAuthenticated');
      // Temporarily disable redirect for debugging
      // window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Auth API
export const authAPI = {
  // Login user
  login: async (credentials) => {
    const response = await api.post('/auth/login', credentials);
    return response.data;
  },

  // Register user
  register: async (userData) => {
    const response = await api.post('/auth/register', userData);
    return response.data;
  },

  // Get current user
  getMe: async () => {
    const response = await api.get('/auth/me');
    return response.data;
  },

  // Update profile
  updateProfile: async (profileData) => {
    const response = await api.put('/auth/profile', profileData);
    return response.data;
  },

  // Change password
  changePassword: async (passwordData) => {
    const response = await api.put('/auth/change-password', passwordData);
    return response.data;
  },

  // Refresh token
  refreshToken: async (refreshToken) => {
    const response = await api.post('/auth/refresh-token', { refreshToken });
    return response.data;
  },

  // Logout
  logout: async (refreshToken) => {
    const response = await api.post('/auth/logout', { refreshToken });
    return response.data;
  },

  // Forgot password
  forgotPassword: async (email) => {
    const response = await api.post('/auth/forgot-password', { email });
    return response.data;
  },

  // Reset password
  resetPassword: async (token, password) => {
    const response = await api.post('/auth/reset-password', { token, password });
    return response.data;
  },
};

// Assets API
export const assetsAPI = {
  // Get all assets with pagination and filtering
  getAssets: async (params = {}) => {
    const response = await api.get('/assets', { params });
    return response.data;
  },

  // Get single asset
  getAsset: async (id) => {
    const response = await api.get(`/assets/${id}`);
    return response.data;
  },

  // Create new asset
  createAsset: async (assetData) => {
    const response = await api.post('/assets', assetData);
    return response.data;
  },

  // Update asset
  updateAsset: async (id, assetData) => {
    const response = await api.put(`/assets/${id}`, assetData);
    return response.data;
  },

  // Delete asset
  deleteAsset: async (id) => {
    const response = await api.delete(`/assets/${id}`);
    return response.data;
  },

  // Add note to asset
  addNote: async (id, note) => {
    const response = await api.post(`/assets/${id}/notes`, { note });
    return response.data;
  },

  // Get asset statistics
  getAssetStats: async () => {
    const response = await api.get('/assets/stats');
    return response.data;
  },

  // Get assets by user
  getAssetsByUser: async (userId, params = {}) => {
    const response = await api.get(`/assets/user/${userId}`, { params });
    return response.data;
  },
};

// Audit API
export const auditAPI = {
  // Fetch audit events grouped for AuditTrails UI
  getAuditOverview: async (params = {}) => {
    const response = await api.get('/audit', { params });
    return response.data;
  }
};

// Utility functions
export const setAuthToken = (token) => {
  if (token) {
    localStorage.setItem('token', token);
    api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
  } else {
    localStorage.removeItem('token');
    delete api.defaults.headers.common['Authorization'];
  }
};

export const setRefreshToken = (refreshToken) => {
  if (refreshToken) {
    localStorage.setItem('refreshToken', refreshToken);
  } else {
    localStorage.removeItem('refreshToken');
  }
};

export const clearAuthTokens = () => {
  localStorage.removeItem('token');
  localStorage.removeItem('refreshToken');
  localStorage.removeItem('user');
  localStorage.removeItem('isAuthenticated');
  delete api.defaults.headers.common['Authorization'];
};

export default api;
